<template>
    <div class="max-w-2xl w-full text-center">
        <div class="bg-white border rounded-2xl p-6 mb-6">
            <h1 class="display-serif text-ink mb-2">{{ t("profile.perspective.intro.title") }}</h1>
            <p class="body-small text-ink-muted mb-2 md:mb-5">{{ t("profile.perspective.intro.description") }}</p>

            <HorizontalDivider as="hr" spacing="none" class="hidden md:block mb-10" />

            <div class="flex flex-col md:flex-row md:justify-center md:items-start divide-y md:divide-y-0 md:divide-x divide-border mb-4 md:mb-10 text-left md:text-center">
                <div
                    v-for="card in cards"
                    :key="card.id"
                    class="flex flex-row md:flex-col items-center gap-4 md:gap-3 py-4 md:py-0 md:px-10 w-full max-w-xs md:max-w-none mx-auto md:mx-0 md:w-52">
                    <div class="size-14 md:h-11 md:w-26 shrink-0 flex items-center justify-center">
                        <component :is="card.icon" class="size-full" />
                    </div>
                    <div class="flex flex-col min-w-0 md:contents">
                        <span class="body-small text-ink-muted">{{ card.step }}</span>
                        <span class="nadia-chat text-ink">{{ card.label }}</span>
                    </div>
                </div>
            </div>
        </div>

        <BaseButton v-if="isCheckingAssessmentStatus" theme="primary" disabled> {{ t("profile.perspective.intro.checkingStatus") }} </BaseButton>
        <BaseButton v-else-if="hasInProgressSession" theme="primary" @click="handleClick('continue')"> {{ t("profile.perspective.intro.continueAssessment") }} </BaseButton>
        <BaseButton v-else theme="primary" :disabled="isStarting" @click="handleClick('start')">
            <span v-if="isStarting">{{ t("profile.perspective.intro.starting") }}</span>
            <span v-else>{{ t("profile.perspective.intro.start") }}</span>
        </BaseButton>

        <p v-if="errorMessage" role="alert" class="body-small text-button-danger mt-3">{{ errorMessage }}</p>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PerspectiveCircles from "~vue/components/PerspectiveCircles.vue";
import PerspectiveStyleCircles from "~vue/components/PerspectiveStyleCircles.vue";
import PerspectiveQuizIcon from "~vue/icons/PerspectiveQuizIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    hasInProgressSession: boolean;
    isCheckingAssessmentStatus?: boolean;
    isStarting: boolean;
    errorMessage: string | null;
    trackIntroFunnel?: boolean;
}

const { isCheckingAssessmentStatus = false, hasInProgressSession = false, isStarting = false, errorMessage = null, trackIntroFunnel = false } = defineProps<Props>();

const { t } = useI18n();

const emit = defineEmits<{
    start: [];
    continue: [];
}>();

onMounted(() => {
    if (trackIntroFunnel) {
        void logUserInteraction(AmplitudeEventNames.PERSPECTIVE_INTRO_VIEWED, { surface: "private_profile" });
    }
});

function handleClick(action: "start" | "continue"): void {
    if (trackIntroFunnel) {
        void logUserInteraction(AmplitudeEventNames.PERSPECTIVE_INTRO_CLICKED, { surface: "private_profile", action });
    }
    if (action === "start") {
        emit("start");
    } else {
        emit("continue");
    }
}

const cards = computed(() =>
    [
        { id: "quiz", icon: PerspectiveQuizIcon, label: t("profile.perspective.intro.steps.quiz") },
        { id: "workingStyle", icon: PerspectiveCircles, label: t("profile.perspective.intro.steps.workingStyle") },
        { id: "coaching", icon: PerspectiveStyleCircles, label: t("profile.perspective.intro.steps.coaching") },
    ].map((card, index) => ({ ...card, step: t("profile.perspective.intro.stepNumber", { number: formatNumber(index + 1, "decimal") }) })),
);
</script>
